import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { EventDetails } from '../types/types';
import { getEventDatabyEventId } from '../api/requests/events';

interface EventContextType {
  eventData: EventDetails | null;
  loading: boolean;
  error: string | null;
  refreshEventData: () => Promise<void>;
  setEventData: React.Dispatch<React.SetStateAction<EventDetails | null>>;
  sportName: string;
  setSportName: React.Dispatch<React.SetStateAction<string>>;
  tournamentType: string;
  setTournamentType: React.Dispatch<React.SetStateAction<string>>;
}

const EventContext = createContext<EventContextType | undefined>(undefined);

interface EventProviderProps {
  children: React.ReactNode;
}

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const [eventData, setEventData] = useState<EventDetails | null>(null);
  const [sportName, setSportName] = useState<string>('');
  const [tournamentType, setTournamentType] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEventData = async () => {
    if (!eventId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getEventDatabyEventId(Number(eventId));
      if (response?.isSuccess) {
        setEventData(response.value || null);
      } else {
        setError(response?.error || 'Failed to fetch event data');
      }
    } catch (err) {
      setError('An error occurred while fetching event data');
      console.error('Error fetching event data:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshEventData = async () => {
    await fetchEventData();
  };

  useEffect(() => {
    fetchEventData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const value: EventContextType = {
    eventData,
    loading,
    error,
    refreshEventData,
    setEventData,
    sportName,
    setSportName,
    tournamentType,
    setTournamentType,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};

export const useEventContext = (): EventContextType => {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error('useEventContext must be used within an EventProvider');
  }
  return context;
};
